import React, { useState, useEffect } from 'react';
import escalationService, { Escalation } from '../../services/escalationService';
import {
  ExclamationTriangleIcon,
  ArrowPathIcon,
  ClockIcon,
  CheckCircleIcon,
  UserIcon,
  FunnelIcon,
  MagnifyingGlassIcon,
} from '@heroicons/react/24/outline';

const AdminEscalationsPage: React.FC = () => {
  const [escalations, setEscalations] = useState<Escalation[]>([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [selectedEscalation, setSelectedEscalation] = useState<Escalation | null>(null);
  const [modalAction, setModalAction] = useState<'assign' | 'resolve' | 'view' | null>(null);
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterPriority, setFilterPriority] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [assignedTo, setAssignedTo] = useState('');
  const [resolution, setResolution] = useState('');

  useEffect(() => {
    loadEscalations();
  }, [filterStatus, filterPriority]);

  const loadEscalations = async () => {
    try {
      setLoading(true);
      const params: any = {};
      if (filterStatus !== 'all') params.status = filterStatus.toUpperCase();
      if (filterPriority !== 'all') params.priority = filterPriority.toUpperCase();
      
      const data = await escalationService.getAll(params);
      setEscalations(data);
    } catch (error) {
      console.error('Failed to load escalations:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleAssign = async () => {
    if (!selectedEscalation || !assignedTo) {
      alert('Please enter an admin user ID');
      return;
    }
    
    try {
      await escalationService.assign(selectedEscalation.id, { assignedTo });
      setShowModal(false);
      setAssignedTo('');
      setSelectedEscalation(null);
      loadEscalations();
    } catch (error) {
      console.error('Failed to assign escalation:', error);
      alert('Failed to assign escalation');
    }
  };

  const handleResolve = async () => {
    if (!selectedEscalation || !resolution) {
      alert('Please provide resolution notes');
      return;
    }
    
    try {
      await escalationService.resolve(selectedEscalation.id, { resolution });
      setShowModal(false);
      setResolution('');
      setSelectedEscalation(null);
      loadEscalations();
    } catch (error) {
      console.error('Failed to resolve escalation:', error);
      alert('Failed to resolve escalation');
    }
  };

  const handleStartWork = async (id: string) => {
    try {
      await escalationService.startWork(id);
      loadEscalations();
    } catch (error) {
      console.error('Failed to start work:', error);
      alert('Failed to start work on escalation');
    }
  };

  const getPriorityBadge = (priority: string) => {
    const badges: Record<string, { color: string; text: string }> = {
      LOW: { color: 'bg-gray-100 text-gray-800', text: 'Low' },
      MEDIUM: { color: 'bg-blue-100 text-blue-800', text: 'Medium' },
      HIGH: { color: 'bg-orange-100 text-orange-800', text: 'High' },
      URGENT: { color: 'bg-red-100 text-red-800', text: '🚨 Urgent' },
    };
    const badge = badges[priority] || badges.MEDIUM;
    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${badge.color}`}>
        {badge.text}
      </span>
    );
  };

  const getStatusBadge = (status: string) => {
    const badges: Record<string, { color: string; icon: any; text: string }> = {
      PENDING: { color: 'bg-yellow-100 text-yellow-800', icon: ClockIcon, text: 'Pending' },
      ASSIGNED: { color: 'bg-blue-100 text-blue-800', icon: UserIcon, text: 'Assigned' },
      IN_PROGRESS: { color: 'bg-purple-100 text-purple-800', icon: ArrowPathIcon, text: 'In Progress' },
      RESOLVED: { color: 'bg-green-100 text-green-800', icon: CheckCircleIcon, text: 'Resolved' },
      CLOSED: { color: 'bg-gray-100 text-gray-800', icon: CheckCircleIcon, text: 'Closed' },
    };
    const badge = badges[status] || badges.PENDING;
    const Icon = badge.icon;
    
    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${badge.color}`}>
        <Icon className="w-4 h-4 mr-1" />
        {badge.text}
      </span>
    );
  };

  const filteredEscalations = escalations.filter(esc => {
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      return (
        esc.reason.toLowerCase().includes(query) ||
        esc.ticketId.toLowerCase().includes(query)
      );
    }
    return true;
  });

  const stats = {
    total: escalations.length,
    pending: escalations.filter(e => e.status === 'PENDING').length,
    inProgress: escalations.filter(e => e.status === 'IN_PROGRESS').length,
    urgent: escalations.filter(e => e.priority === 'URGENT').length,
  };

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <ExclamationTriangleIcon className="h-10 w-10 text-orange-600 mr-3" />
              <div>
                <h1 className="text-3xl font-bold text-gray-900">Escalation Dashboard</h1>
                <p className="text-gray-600 mt-1">Manage support escalations and priority issues</p>
              </div>
            </div>
            <button
              onClick={loadEscalations}
              className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
            >
              <ArrowPathIcon className="h-4 w-4 mr-2" />
              Refresh
            </button>
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
          <div className="bg-white rounded-lg shadow p-6">
            <p className="text-sm font-medium text-gray-600">Total</p>
            <p className="text-2xl font-bold text-gray-900">{stats.total}</p>
          </div>
          <div className="bg-white rounded-lg shadow p-6">
            <p className="text-sm font-medium text-gray-600">Pending</p>
            <p className="text-2xl font-bold text-yellow-600">{stats.pending}</p>
          </div>
          <div className="bg-white rounded-lg shadow p-6">
            <p className="text-sm font-medium text-gray-600">In Progress</p>
            <p className="text-2xl font-bold text-purple-600">{stats.inProgress}</p>
          </div>
          <div className="bg-white rounded-lg shadow p-6">
            <p className="text-sm font-medium text-gray-600">Urgent</p>
            <p className="text-2xl font-bold text-red-600">{stats.urgent}</p>
          </div>
        </div>

        {/* Filters */}
        <div className="bg-white rounded-lg shadow mb-6 p-4">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="flex-1">
              <div className="relative">
                <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search by ticket ID or reason..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                />
              </div>
            </div>
            <div className="flex items-center gap-2">
              <FunnelIcon className="h-5 w-5 text-gray-400" />
              <select
                value={filterStatus}
                onChange={(e) => setFilterStatus(e.target.value)}
                className="px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-orange-500"
              >
                <option value="all">All Status</option>
                <option value="pending">Pending</option>
                <option value="assigned">Assigned</option>
                <option value="in_progress">In Progress</option>
                <option value="resolved">Resolved</option>
                <option value="closed">Closed</option>
              </select>
              <select
                value={filterPriority}
                onChange={(e) => setFilterPriority(e.target.value)}
                className="px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-orange-500"
              >
                <option value="all">All Priority</option>
                <option value="urgent">Urgent</option>
                <option value="high">High</option>
                <option value="medium">Medium</option>
                <option value="low">Low</option>
              </select>
            </div>
          </div>
        </div>

        {/* Escalations Table */}
        <div className="bg-white rounded-lg shadow overflow-hidden">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <ArrowPathIcon className="h-8 w-8 text-gray-400 animate-spin" />
              <span className="ml-2 text-gray-600">Loading escalations...</span>
            </div>
          ) : filteredEscalations.length === 0 ? (
            <div className="text-center py-12">
              <ExclamationTriangleIcon className="mx-auto h-12 w-12 text-gray-400" />
              <h3 className="mt-2 text-sm font-medium text-gray-900">No escalations found</h3>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Priority</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Ticket</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Reason</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Assigned</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {filteredEscalations.map((esc) => (
                    <tr key={esc.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        {getPriorityBadge(esc.priority)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {esc.ticketId}
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-900 max-w-xs truncate">
                        {esc.reason}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {getStatusBadge(esc.status)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {esc.assignedUser?.displayName || 'Unassigned'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm space-x-2">
                        {esc.status === 'PENDING' && (
                          <button
                            onClick={() => {
                              setSelectedEscalation(esc);
                              setModalAction('assign');
                              setShowModal(true);
                            }}
                            className="text-blue-600 hover:text-blue-900"
                          >
                            Assign
                          </button>
                        )}
                        {esc.status === 'ASSIGNED' && (
                          <button
                            onClick={() => handleStartWork(esc.id)}
                            className="text-purple-600 hover:text-purple-900"
                          >
                            Start Work
                          </button>
                        )}
                        {esc.status === 'IN_PROGRESS' && (
                          <button
                            onClick={() => {
                              setSelectedEscalation(esc);
                              setModalAction('resolve');
                              setShowModal(true);
                            }}
                            className="text-green-600 hover:text-green-900"
                          >
                            Resolve
                          </button>
                        )}
                        <button
                          onClick={() => {
                            setSelectedEscalation(esc);
                            setModalAction('view');
                            setShowModal(true);
                          }}
                          className="text-gray-600 hover:text-gray-900"
                        >
                          View
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Action Modal */}
        {showModal && selectedEscalation && (
          <div className="fixed inset-0 z-50 overflow-y-auto">
            <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20">
              <div className="fixed inset-0 bg-gray-500 bg-opacity-75" onClick={() => setShowModal(false)} />
              
              <div className="inline-block bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:max-w-2xl sm:w-full">
                <div className="bg-white px-4 pt-5 pb-4 sm:p-6">
                  <h3 className="text-lg font-medium text-gray-900 mb-4">
                    {modalAction === 'assign' && 'Assign Escalation'}
                    {modalAction === 'resolve' && 'Resolve Escalation'}
                    {modalAction === 'view' && 'Escalation Details'}
                  </h3>
                  
                  <div className="space-y-4">
                    <div className="bg-gray-50 p-4 rounded-md">
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <p className="text-sm font-medium text-gray-700">Ticket ID</p>
                          <p className="text-sm text-gray-900">{selectedEscalation.ticketId}</p>
                        </div>
                        <div>
                          <p className="text-sm font-medium text-gray-700">Priority</p>
                          {getPriorityBadge(selectedEscalation.priority)}
                        </div>
                        <div className="col-span-2">
                          <p className="text-sm font-medium text-gray-700">Reason</p>
                          <p className="text-sm text-gray-900 mt-1">{selectedEscalation.reason}</p>
                        </div>
                      </div>
                    </div>

                    {modalAction === 'assign' && (
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Assign to Admin User ID
                        </label>
                        <input
                          type="text"
                          value={assignedTo}
                          onChange={(e) => setAssignedTo(e.target.value)}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md"
                          placeholder="Enter admin user ID"
                        />
                      </div>
                    )}

                    {modalAction === 'resolve' && (
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Resolution Notes
                        </label>
                        <textarea
                          value={resolution}
                          onChange={(e) => setResolution(e.target.value)}
                          rows={4}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md"
                          placeholder="Describe how the issue was resolved..."
                        />
                      </div>
                    )}

                    {selectedEscalation.resolution && (
                      <div className="bg-green-50 p-4 rounded-md">
                        <p className="text-sm font-medium text-green-900">Resolution</p>
                        <p className="text-sm text-green-700 mt-1">{selectedEscalation.resolution}</p>
                      </div>
                    )}
                  </div>
                </div>
                
                <div className="bg-gray-50 px-4 py-3 sm:px-6 flex justify-end gap-2">
                  {modalAction === 'assign' && (
                    <button
                      onClick={handleAssign}
                      disabled={!assignedTo}
                      className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50"
                    >
                      Assign
                    </button>
                  )}
                  {modalAction === 'resolve' && (
                    <button
                      onClick={handleResolve}
                      disabled={!resolution}
                      className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 disabled:opacity-50"
                    >
                      Resolve
                    </button>
                  )}
                  <button
                    onClick={() => {
                      setShowModal(false);
                      setSelectedEscalation(null);
                      setModalAction(null);
                      setAssignedTo('');
                      setResolution('');
                    }}
                    className="px-4 py-2 border border-gray-300 rounded-md hover:bg-gray-50"
                  >
                    {modalAction === 'view' ? 'Close' : 'Cancel'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminEscalationsPage;
